import PocketBase from 'pocketbase';

let pb: PocketBase | null = null;
let connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
let lastError: string | null = null;
let lastSync: string | null = null;

const POCKETBASE_URL = import.meta.env.VITE_POCKETBASE_URL;

export interface PocketBaseUser {
  id: string;
  email: string;
  name: string;
  verified: boolean;
}

export const initializePocketBase = async (): Promise<PocketBase | null> => {
  if (!POCKETBASE_URL) {
    connectionStatus = 'disconnected';
    return null;
  }
  
  if (pb) return pb;
  
  try {
    connectionStatus = 'connecting';
    pb = new PocketBase(POCKETBASE_URL);
    await pb.health.check();
    
    // Preserve existing auth store - do not auto-refresh or clear on failure
    // PocketBase SDK handles session persistence natively
    
    connectionStatus = 'connected';
    lastError = null;
    lastSync = new Date().toISOString();
    return pb;
  } catch (error) {
    connectionStatus = 'error';
    lastError = error instanceof Error ? error.message : 'Unknown error';
    console.error('PocketBase connection failed:', lastError);
    throw new Error(`PocketBase connection failed: ${lastError}`);
  }
};

export const getPocketBase = async (): Promise<PocketBase | null> => {
  if (!POCKETBASE_URL) return null;
  if (pb) return pb;
  return initializePocketBase();
};

export const getConnectionStatus = () => ({
  status: connectionStatus,
  url: POCKETBASE_URL || null,
  lastError,
  lastSync,
  isMockMode: !POCKETBASE_URL,
  isAuthenticated: pb?.authStore.isValid || false,
  currentUser: pb?.authStore.model as unknown as PocketBaseUser | null
});

export const login = async (email: string, password: string): Promise<PocketBaseUser> => {
  const client = await getPocketBase();
  if (!client) throw new Error('PocketBase not configured');
  
  const authData = await client.collection('users').authWithPassword(email, password);
  
  // Clean up any stale session timestamp
  localStorage.removeItem('invoiceflow_session_started_at');
  
  return authData.record as unknown as PocketBaseUser;
};

export const logout = () => {
  if (typeof window !=='undefined') {
    window.dispatchEvent(new CustomEvent('invoiceflow:auth-transition',{detail: {type: 'logout'}}));
  }
  pb?.authStore.clear();
  // Clean up any stale session timestamp
  localStorage.removeItem('invoiceflow_session_started_at');
};

export const isAuthenticated = (): boolean => pb?.authStore.isValid || false;

export const getCurrentAuthToken = (): string => pb?.authStore.token || '';

export const requestPasswordReset = async (email: string) => {
  if (!email) throw new Error('Email is required');
  const client = await getPocketBase();
  if (!client) throw new Error('PocketBase not configured');
  await client.collection('users').requestPasswordReset(email);
};

export const getProtectedFileUrl = async (record: any, filename: string, download = false) => {
  const client = await getPocketBase();
  if (!client) return '';
  const token = await client.files.getToken();
  return client.files.getURL(record, filename, { token, download });
};